<template>
  <v-card v-if="showSelector && packlists.length > 1" class="packlist-selector" elevation="8">
    <v-card-title class="text-h6 pb-2">
      <v-icon left>mdi-format-list-bulleted-square</v-icon>
      Select a List to View
    </v-card-title>
    
    <v-card-text class="pt-2">
      <v-list class="py-0">
        <v-list-item
          v-for="(packlist, index) in packlists"
          :key="packlist.id"
          :class="{ 'v-list-item--active': selectedIndex === index }"
          @click="selectPacklist(index)"
          class="mb-1"
        >
          <template v-slot:prepend>
            <v-icon 
              :color="selectedIndex === index ? 'primary' : 'grey'"
              class="mr-3"
            >
              {{ selectedIndex === index ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
            </v-icon>
          </template>
          
          <v-list-item-content>
            <v-list-item-title class="font-weight-medium">
              {{ getPacklistDisplayName(packlist) }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="packlist.description">
              {{ packlist.description }}
            </v-list-item-subtitle>
            <v-list-item-subtitle class="text-caption mt-1">
              {{ packlist.items?.length || 0 }} items
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card-text>
    
    <v-card-actions class="pt-0">
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="text"
        @click="closeSelector"
      >
        Close
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { ref, computed, watch } from 'vue'

export default {
  name: 'PacklistSelector',
  props: {
    packlists: {
      type: Array,
      default: () => []
    },
    selectedIndex: {
      type: Number,
      default: 0
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:selectedIndex', 'update:show', 'select'],
  setup(props, { emit }) {
    const showSelector = computed({
      get: () => props.show,
      set: (value) => emit('update:show', value)
    })
    
    const formatDuration = (days) => {
      if (!days || days === 1) return ''
      
      if (days === 7) return ' (1 week)'
      if (days === 14) return ' (2 weeks)'
      if (days === 21) return ' (3 weeks)'
      if (days === 28) return ' (4 weeks)'
      
      // For weeks + days
      if (days > 7 && days % 7 === 0) {
        const weeks = Math.floor(days / 7)
        return ` (${weeks} weeks)`
      }
      
      // For mixed weeks and days
      if (days > 7) {
        const weeks = Math.floor(days / 7)
        const remainingDays = days % 7
        if (remainingDays === 0) {
          return ` (${weeks} week${weeks !== 1 ? 's' : ''})`
        }
        return ` (${weeks} week${weeks !== 1 ? 's' : ''} and ${remainingDays} day${remainingDays !== 1 ? 's' : ''})`
      }
      
      // Just days
      return ` (${days} days)`
    }
    
    const getPacklistDisplayName = (packlist) => {
      if (!packlist) return ''
      return packlist.name + formatDuration(packlist.duration)
    }
    
    const selectPacklist = (index) => {
      emit('update:selectedIndex', index)
      emit('select', index)
    }
    
    const closeSelector = () => {
      showSelector.value = false
    }
    
    return {
      showSelector,
      getPacklistDisplayName,
      selectPacklist,
      closeSelector
    }
  }
}
</script>

<style scoped>
.packlist-selector {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  min-width: 400px;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.v-list-item {
  border-radius: 8px;
  margin-bottom: 4px;
}

.v-list-item--active {
  background-color: rgba(var(--v-theme-primary), 0.1) !important;
}

.v-list-item:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.05);
}

.v-list-item--active:hover {
  background-color: rgba(var(--v-theme-primary), 0.15) !important;
}

@media (max-width: 600px) {
  .packlist-selector {
    min-width: 90vw;
    max-width: 95vw;
  }
}
</style>